export const INVENTORY_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "auction_purchased", label: "Auction Purchased" },
  { value: "purchased", label: "Purchased" },
  { value: "shipping", label: "Shipping" },
  { value: "arrived_at_port", label: "Arrived at Port" },
  { value: "customs_clearance", label: "Customs Clearance" },
  { value: "in_transit_to_showroom", label: "In Transit to Showroom" },
  { value: "received", label: "Received" },
  { value: "preparing", label: "Preparing" },
  { value: "in_stock", label: "In Stock" },
  { value: "reserved", label: "Reserved" },
  { value: "sold", label: "Sold" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
] as const;

export type InventoryStatusValue = (typeof INVENTORY_STATUS_OPTIONS)[number]["value"];

/** Includes legacy `unsold` for reading older products. */
export type InventoryStatusValueOrLegacy = InventoryStatusValue | "unsold";

/**
 * Statuses offered when changing inventory status (current excluded).
 * All options are listed; only the next allowed step is selectable:
 * - Draft → Received
 * - Received → In Stock
 */
export function inventoryStatusChangeOptions(
  currentStatus: string | null | undefined,
): readonly { value: InventoryStatusValue; label: string; disabled: boolean }[] {
  const current = (currentStatus ?? "draft").trim().toLowerCase();
  const allowedNext =
    current === "draft" ? "received" : current === "received" ? "in_stock" : null;

  return INVENTORY_STATUS_OPTIONS.filter((o) => o.value !== current).map((o) => ({
    value: o.value,
    label: o.label,
    disabled: allowedNext != null ? o.value !== allowedNext : false,
  }));
}

const KNOWN = new Set<string>([
  ...INVENTORY_STATUS_OPTIONS.map((o) => o.value),
  "unsold",
]);

export function inventoryStatusLabel(status: string | null | undefined): string {
  const s = status ?? "draft";
  if (s === "hold" || s === "unsold") return "Unsold";
  const opt = INVENTORY_STATUS_OPTIONS.find((o) => o.value === s);
  return opt?.label ?? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function normalizeInventoryStatus(raw: unknown): InventoryStatusValueOrLegacy {
  const s = raw == null || raw === "" ? "draft" : String(raw);
  if (s === "hold") return "unsold";
  return (KNOWN.has(s) ? s : "draft") as InventoryStatusValueOrLegacy;
}

export function inventoryStatusBadgeClass(status: string | null | undefined): string {
  const s = status ?? "draft";
  switch (s) {
    case "sold":
    case "cancelled":
      return "border border-destructive/50 bg-destructive/15 text-destructive";
    case "delivered":
      return "border border-violet-600/40 bg-violet-500/15 text-violet-900 dark:text-violet-200";
    case "reserved":
      return "border border-sky-600/40 bg-sky-500/15 text-sky-800 dark:text-sky-200";
    case "draft":
      return "border border-amber-800/40 bg-amber-500/20 text-amber-950 dark:text-amber-100";
    case "unsold":
      return "border border-orange-800/40 bg-orange-600/15 text-orange-950 dark:text-orange-100";
    case "in_stock":
      return "border border-emerald-700/35 bg-emerald-600/12 text-emerald-900 dark:text-emerald-100";
    case "auction_purchased":
    case "purchased":
    case "shipping":
    case "arrived_at_port":
    case "customs_clearance":
    case "in_transit_to_showroom":
    case "received":
    case "preparing":
      return "border border-slate-500/40 bg-slate-500/12 text-slate-800 dark:text-slate-200";
    default:
      return "border border-emerald-700/35 bg-emerald-600/12 text-emerald-900 dark:text-emerald-100";
  }
}

export function inventoryCardStatusPill(status: string | null | undefined): {
  label: string;
  className: string;
  dotClassName: string;
} {
  const s = status ?? "draft";
  const label = inventoryStatusLabel(s);

  if (s === "sold" || s === "cancelled") {
    return {
      label,
      className: "border border-rose-400/40 bg-rose-600/90 text-white shadow-md shadow-rose-950/20 backdrop-blur-md",
      dotClassName: "bg-rose-300",
    };
  }
  if (s === "delivered") {
    return {
      label,
      className: "border border-violet-400/40 bg-violet-600/90 text-white shadow-md shadow-violet-950/20 backdrop-blur-md",
      dotClassName: "bg-violet-300",
    };
  }
  if (s === "reserved") {
    return {
      label,
      className: "border border-sky-400/40 bg-sky-600/90 text-white shadow-md shadow-sky-950/20 backdrop-blur-md",
      dotClassName: "bg-sky-300 animate-pulse",
    };
  }
  if (s === "draft") {
    return {
      label,
      className: "border border-amber-400/50 bg-amber-500/95 text-amber-950 shadow-md shadow-amber-950/15 backdrop-blur-md font-black",
      dotClassName: "bg-amber-950",
    };
  }
  if (s === "unsold") {
    return {
      label,
      className: "border border-orange-400/40 bg-orange-600/90 text-white shadow-md shadow-orange-950/20 backdrop-blur-md",
      dotClassName: "bg-orange-300",
    };
  }
  if (s === "in_stock") {
    return {
      label,
      className: "border border-emerald-400/40 bg-emerald-600/90 text-white shadow-md shadow-emerald-950/20 backdrop-blur-md",
      dotClassName: "bg-emerald-300 animate-pulse",
    };
  }

  return {
    label,
    className: "border border-slate-400/40 bg-slate-600/90 text-white shadow-md shadow-slate-950/20 backdrop-blur-md",
    dotClassName: "bg-slate-300",
  };
}
